import arrowIcon from '@disy/cadenza-icons/arrow-left.svg';
import filterIcon from '@disy/cadenza-icons/filter.svg';
import resetIcon from '@disy/cadenza-icons/reset.svg';
import h from 'hyperscript';

import 'ui/sidebar/sidebar';
import 'ui/compact-header/compact-header.css';

import { EXECUTION_CONFIRMED } from 'cadenza/api-client/api-constants';
import { FilterPanelApi } from 'cadenza/api-client/filter-panel-api';
import { setLayerConditions } from 'cadenza/api-client/layer-conditions-api';
import { getCurrentHierarchyParameters } from 'cadenza/api-client/processing-param-util';
import { CONDITIONS_HIERARCHIES_CHANGE } from 'cadenza/conditions/event-types';
import { FilterController } from 'cadenza/conditions/filter-controller';
import { MAP } from 'cadenza/map-dashboard/event-types';
import { OnDemandView } from 'cadenza/on-demand-view/on-demand-view';
import { context } from 'cadenza/map/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { refreshLayer, zoomToResult } from 'cadenza/map/layer-utils';
import { MapProperty } from 'cadenza/map/map-property';
import { on, unByKey } from 'cadenza/utils/event-util';
import { commonI18n } from 'cadenza/utils/i18n/i18n';
import { icon } from 'cadenza/utils/icon/icon';
import { logger } from 'cadenza/utils/logging';
import { setParam } from 'cadenza/utils/url-param-utils';
import { mediaXsOnly } from 'cadenza/utils/media';
import { getSubnav } from 'cadenza/utils/dependency-registry';
import { EXTERNAL_CONDITION_CHANGE } from 'cadenza/map/custom/map-message-dispatcher/external-condition-change-event';

import { LayerAction } from './layer-action';
import i18n from './layer-actions.properties';

export class Filter extends LayerAction {

  #map;
  #sidebar;
  #sidebarContent;
  #onDemandView;
  #eventKeys;

  constructor (layer, options) {
    super(layer, Object.assign({
      label: i18n('filter'),
      icon: icon(filterIcon)
    }, options));
    this.#map = options.map;
    this.#sidebar = document.getElementById('sidebar');
  }

  /** @return {Promise} - resolves after opening the dialog finished. */
  execute () {
    if (this.#sidebarContent) {
      this._closeFilterPanel();
      return;
    }
    const layer = this._layer;
    const conditionDefinitionSetId = layer.get(LayerProperty.CONDITION_DEFINITION_SET_ID);
    const conditionHash = layer.get(LayerProperty.CONDITION_VALUES_SET_HASH);

    return FilterPanelApi.getContent(conditionDefinitionSetId, conditionHash, getCurrentHierarchyParameters(), false)
      .then(filterPanelContents => {
        const filterController = new FilterController(
          layer.get(LayerProperty.INITIAL_CONDITION_VALUES_SET_HASH),
          layer.get(LayerProperty.CONDITION_VALUES_SET_HASH));
        filterController.setAttribute('has-header', true);
        filterController.setAttribute('layer-id', layer.get(LayerProperty.ID));
        filterController.classList.add('light-bg');

        const filterPanelAttributes = {
          open: true,
          onclose: () => this._closeFilterPanel(),
          attrs: {
            'aria-hidden': 'false',
            'open-on-mobile': 'true'
          }
        };
        const sidebarContent = h('d-sidebar.is-overlay', filterPanelAttributes, filterController);

        const filterPanel = createFilterPanel(sidebarContent, layer, filterPanelContents);
        filterController.append(...filterPanel);
        this.#sidebar.append(sidebarContent);
        this.#sidebarContent = sidebarContent;

        layer.on(EXTERNAL_CONDITION_CHANGE, event => reload(event, filterController, sidebarContent, layer));

        const isOnDemand = layer.get(LayerProperty.SELECTOR_EXECUTION_TYPE) === 'onDemand' || mediaXsOnly.matches;

        if (isOnDemand) {
          // the current execution state is confirmed
          setParam({ paramName: EXECUTION_CONFIRMED, paramValue: 'true' });

          this.#onDemandView = new OnDemandView();
          this.#onDemandView.setAttribute('hidden-element-selector', '#map');
          this.#map.getTarget().after(this.#onDemandView);
        }

        this.#eventKeys = [
          on(this.#map, `change:${MapProperty.EDITING_LAYER}`, () => this._closeFilterPanel()),
          on(filterController, CONDITIONS_HIERARCHIES_CHANGE, event => this._onFilterChange(event))
        ];
        const subnav = getSubnav();
        if (subnav) {
          subnav.registerFilterControllerEvents(filterController);
        }
      }).catch(error => {
        logger.error('Failed to retrieve filter panel contents.', error);
        throw error;
      });
  }

  _closeFilterPanel () {
    unByKey(this.#eventKeys);
    this.#sidebar.removeChild(this.#sidebarContent);
    this.#sidebarContent = null;

    if (this.#onDemandView) {
      this.#onDemandView.remove();
      this.#onDemandView = null;
    }
  }

  _onFilterChange (event) {
    if (event.defaultPrevented) {
      return;
    }

    const map = this.#map;
    const layer = this._layer;
    const conditionValuesSetHash = event.detail.conditionValuesSetHash;
    layer.set(LayerProperty.CONDITION_VALUES_SET_HASH, conditionValuesSetHash);
    const viewId = layer.get(LayerProperty.ID);
    const layerToUpdate = layer.get(LayerProperty.ID);

    setLayerConditions(layerToUpdate, conditionValuesSetHash)
      .then(() => {
        event.target.notifyFiltersApplied();
        return context(map).pull();
      })
      .then(() => {
        refreshLayer(layer);
        zoomToResult(map, layer);
        map.getTarget().dispatchEvent(new CustomEvent(MAP.FILTER_CONDITION_CHANGED, {
          detail: {
            conditionValuesSetHash,
            viewId
          }
        }));
      })
      .catch(error => logger.error('Failed to update the condition values hash on backend.', error));
  }

  isSupportedSync ({ isEditingLayer }) {
    return !isEditingLayer && this._layer.get(LayerProperty.IS_FILTERABLE);
  }

}

function createFilterPanel (sidebarContent, layer, filterPanelContents) {
  return [
    h('header.d-compact-header', [
      h('button.d-sidebar-back.button.button-icon.button-borderless', {
        type: 'button',
        onclick: () => sidebarContent.close(),
        title: commonI18n('back')
      }, icon(arrowIcon)),
      h('label.d-compact-header--title.ellipsis', layer.get(LayerProperty.NAME)),
      h('button.d-sidebar-reset.button.button-icon.button-borderless', {
        type: 'button'
      }, icon(resetIcon))
    ]),
    h('.d-sidebar-content', filterPanelContents),
    h('footer.d-sidebar-footer', [
      h('button.button.button-primary.mobile-filter', commonI18n('common:apply')),
      h('button.button.button-secondary.mobile-filter', commonI18n('common:cancel'))
    ])
  ];
}

function reload (event, filterController, sidebarContent, layer) {
  const conditionDefinitionSetId = layer.get(LayerProperty.CONDITION_DEFINITION_SET_ID);
  FilterPanelApi.getContent(conditionDefinitionSetId, event.newConditionValuesSetHash, getCurrentHierarchyParameters(), false)
    .then(newFilterPanelContent => {
      filterController.innerHTML = '';
      filterController.append(...createFilterPanel(sidebarContent, layer, newFilterPanelContent));
    }).catch(error => {
      logger.error('Failed to retrieve filter panel contents.', error);
    });
}
